/**
 * Child Profile Service
 * Handles child profile CRUD operations and settings
 */

import { apiClient, extractData, withRetry } from './api';
import type {
  ParentSettings,
  LearningProgress,
  Subject,
  ChildProfileWithConsent,
  ConsentRequest,
  ConsentResponse
} from '@kids-lms/types';

export interface CreateChildRequest {
  name: string;
  dateOfBirth: string; // ISO date string
  gradeLevel: number;
  allowedSubjects?: Subject[];
  dailyTimeLimit?: number;
  avatarUrl?: string;
  themePreference?: 'light' | 'dark' | 'system';
  parentalConsent?: boolean;
}

export interface UpdateChildRequest {
  name?: string;
  gradeLevel?: number;
  allowedSubjects?: Subject[];
  dailyTimeLimit?: number;
  avatarUrl?: string;
  themePreference?: 'light' | 'dark' | 'system';
}

export interface UpdateSettingsRequest {
  allowedTopics?: string[];
  blockedKeywords?: string[];
  notificationPreferences?: {
    dailySummary?: boolean;
    weeklyReport?: boolean;
    flaggedContent?: boolean;
    sessionStart?: boolean;
    timeLimit?: boolean;
  };
}

/**
 * Get all children for the current parent
 */
export async function getChildren(): Promise<ChildProfileWithConsent[]> {
  const response = await withRetry(() =>
    apiClient.get<{ success: boolean; data: ChildProfileWithConsent[] }>('/children')
  );
  return extractData(response);
}

/**
 * Get a specific child profile
 */
export async function getChild(childId: string): Promise<ChildProfileWithConsent> {
  const response = await withRetry(() =>
    apiClient.get<{ success: boolean; data: ChildProfileWithConsent }>(`/children/${childId}`)
  );
  return extractData(response);
}

/**
 * Create a new child profile
 */
export async function createChild(data: CreateChildRequest): Promise<ChildProfileWithConsent> {
  const response = await apiClient.post<{ success: boolean; data: ChildProfileWithConsent }>(
    '/children',
    data
  );
  return extractData(response);
}

/**
 * Update a child profile
 */
export async function updateChild(childId: string, data: UpdateChildRequest): Promise<ChildProfileWithConsent> {
  const response = await apiClient.patch<{ success: boolean; data: ChildProfileWithConsent }>(
    `/children/${childId}`,
    data
  );
  return extractData(response);
}

/**
 * Delete a child profile
 */
export async function deleteChild(childId: string): Promise<{ deleted: boolean }> {
  const response = await apiClient.delete<{ success: boolean; data: { deleted: boolean } }>(
    `/children/${childId}`
  );
  return extractData(response);
}

/**
 * Get learning progress for a child
 */
export async function getProgress(childId: string): Promise<LearningProgress[]> {
  const response = await withRetry(() =>
    apiClient.get<{ success: boolean; data: LearningProgress[] }>(`/children/${childId}/progress`)
  );
  return extractData(response);
}

/**
 * Get parent settings for a child
 */
export async function getSettings(childId: string): Promise<ParentSettings> {
  const response = await withRetry(() =>
    apiClient.get<{ success: boolean; data: ParentSettings }>(`/children/${childId}/settings`)
  );
  return extractData(response);
}

/**
 * Update parent settings for a child
 */
export async function updateSettings(childId: string, data: UpdateSettingsRequest): Promise<ParentSettings> {
  const response = await apiClient.patch<{ success: boolean; data: ParentSettings }>(
    `/children/${childId}/settings`,
    data
  );
  return extractData(response);
}

/**
 * Provide COPPA consent for a child
 */
export async function provideConsent(childId: string, data: ConsentRequest): Promise<ConsentResponse> {
  const response = await apiClient.post<{ success: boolean; data: ConsentResponse }>(
    `/children/${childId}/consent`,
    data
  );
  return extractData(response);
}

/**
 * Get COPPA consent status for a child
 */
export async function getConsentStatus(childId: string): Promise<{
  childId: string;
  childName: string;
  age: number;
  requiresConsent: boolean;
  consentGiven: boolean;
  consentDate?: string;
  canUsePlatform: boolean;
}> {
  const response = await withRetry(() =>
    apiClient.get<{ success: boolean; data: {
      childId: string;
      childName: string;
      age: number;
      requiresConsent: boolean;
      consentGiven: boolean;
      consentDate?: string;
      canUsePlatform: boolean;
    } }>(`/children/${childId}/consent`)
  );
  return extractData(response);
}

export default {
  getChildren,
  getChild,
  createChild,
  updateChild,
  deleteChild,
  getProgress,
  getSettings,
  updateSettings,
  provideConsent,
  getConsentStatus,
};
